import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye } from "lucide-react";
import { API_URL } from "../config/api";
import { GAME_LEVELS } from "../constants/gameLevels";
import { humanizeGemLabel } from "../constants/gems";
import MenuIconTooltip from "./MenuIconTooltip";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import GameProgressChart from "./GameProgressChart";
import GameStatsModeSwitch from "./GameStatsModeSwitch";
import MasteryMatrixChart from "./MasteryMatrixChart";
import MatrixPoolFilterDropdown from "./MatrixPoolFilterDropdown";
import MemoryScoreChart from "./MemoryScoreChart";
import PackAssignDropdown from "./PackAssignDropdown";
import { ProgressDeltaCell } from "./GameProgressDelta";
import {
  filterWordsForGameLevelScope,
  readMatrixPoolFilter,
  readMatrixPoolScope,
} from "../utils/gameWordPool";
import {
  computeProgressCountDelta,
  formatAverageScore,
  formatPlayTime,
  formatProgressDelta,
  getMonthStartMs,
  getRollingDaysCutoffMs,
} from "../utils/gameProgressDelta";
import { formatSessionDate, formatSessionTime } from "../utils/parisWallClock";
import "./GameDifficultySelect.css";
import "./GameProgressDelta.css";
import "./GameProgressChart.css";
import "./GameStatsModeSwitch.css";
import "./VocabTooltip.css";
import "./GameStatsView.css";

const HISTORY_LEVEL_FILTER_OPTIONS = [
  { value: "all", label: "Tous les niveaux" },
  ...GAME_LEVELS.map((level) => ({
    value: String(level.id),
    label: level.name,
  })),
];

const HISTORY_DATE_FILTER_OPTIONS = [
  { value: "all", label: "Toutes les dates" },
  { value: "today", label: "Aujourd'hui" },
  { value: "week", label: "Semaine actuelle" },
  { value: "month", label: "Mois actuel" },
];

const TABLE_COLUMNS = (
  <colgroup>
    <col className="game-stats-table__col-date" />
    <col className="game-stats-table__col-time" />
    <col className="game-stats-table__col-level" />
    <col className="game-stats-table__col-score" />
    <col className="game-stats-table__col-discovery" />
    <col className="game-stats-table__col-mastery" />
    <col className="game-stats-table__col-gem" />
    <col className="game-stats-table__col-pool" />
  </colgroup>
);

function buildHistoryGem(session) {
  const id = String(session?.chestGem?.id || "").trim();
  if (!id) return null;
  const filename = `${id}.png`;
  return {
    id,
    label: String(session.chestGem.label || "").trim() || humanizeGemLabel(filename),
    imageUrl: `${process.env.PUBLIC_URL || ""}/gems/${encodeURIComponent(filename)}`,
  };
}

function HistoryGemCell({ session }) {
  const gem = buildHistoryGem(session);
  if (!gem) {
    return <td className="game-stats-gem">—</td>;
  }
  return (
    <td className="game-stats-gem">
      <MenuIconTooltip tooltip={gem.label} placement="above" portal>
        <img
          src={gem.imageUrl}
          alt=""
          className="game-stats-gem-thumb"
          decoding="sync"
        />
      </MenuIconTooltip>
    </td>
  );
}

function GameStatsSkel({ className = "", style }) {
  return <span className={`game-stats-skel ${className}`.trim()} style={style} />;
}

const HISTORY_SKELETON_LEVEL_WIDTHS = [118, 96, 128, 108, 88, 122, 104, 114];
const INDICATOR_SKELETON_LABEL_WIDTHS = [
  228, 312, 258, 322, 268, 214, 226, 148,
];

function GameStatsHistorySkeleton({
  statsMode,
  onStatsModeChange,
  levelFilter,
  dateFilter,
  onLevelFilterChange,
  onDateFilterChange,
}) {
  const isMemoryMode = statsMode === "memory";

  return (
    <div className="game-stats-view game-stats-view--table game-stats-view--skeleton">
      <section className="game-stats-history">
        <header className="game-stats-history__header">
          <div className="game-stats-history__title-row">
            <h2 className="game-stats-title game-stats-history__title">
              Historique des parties jouées
            </h2>
            <p className="game-stats-history__count">
              <GameStatsSkel className="game-stats-skel--count" />
            </p>
          </div>
          <div className="game-stats-history__filters">
            <div className="game-stats-history__filter">
              <div className="game-stats-history__filter-field">
                <span className="game-stats-history__filter-label">Niveau</span>
                <div className="game-stats-history__filter-dropdown">
                  <PackAssignDropdown
                    options={HISTORY_LEVEL_FILTER_OPTIONS}
                    value={levelFilter}
                    onChange={onLevelFilterChange}
                    ariaLabel="Filtrer l'historique par niveau"
                    matchTriggerWidth
                  />
                </div>
              </div>
              <div className="game-stats-history__filter-field">
                <span className="game-stats-history__filter-label">Date</span>
                <div className="game-stats-history__filter-dropdown game-stats-history__filter-dropdown--date">
                  <PackAssignDropdown
                    options={HISTORY_DATE_FILTER_OPTIONS}
                    value={dateFilter}
                    onChange={onDateFilterChange}
                    ariaLabel="Filtrer l'historique par date"
                    matchTriggerWidth
                  />
                </div>
              </div>
            </div>
            <div className="game-stats-history__mode">
              <GameStatsModeSwitch mode={statsMode} onChange={onStatsModeChange} />
            </div>
          </div>
        </header>

        <div className="game-stats-table-shell">
          <div className="game-stats-table-head">
            <table className="game-stats-table">
              {TABLE_COLUMNS}
              <GameStatsTableHead isMemoryMode={isMemoryMode} />
            </table>
          </div>
          <div className="game-stats-table-body">
            <div className="game-stats-table-wrap">
              <table className="game-stats-table">
                {TABLE_COLUMNS}
                <tbody>
                  {HISTORY_SKELETON_LEVEL_WIDTHS.map((levelWidth, index) => (
                    <tr key={index}>
                      <td>
                        <GameStatsSkel className="game-stats-skel--date" />
                      </td>
                      <td>
                        <GameStatsSkel className="game-stats-skel--time" />
                      </td>
                      <td>
                        <GameStatsSkel
                          className="game-stats-skel--level"
                          style={{ width: `${levelWidth}px` }}
                        />
                      </td>
                      <td className="game-stats-score">
                        <GameStatsSkel className="game-stats-skel--score" />
                      </td>
                      {isMemoryMode ? (
                        <>
                          <td className="game-stats-table__spacer" aria-hidden="true" />
                          <td className="game-stats-table__spacer" aria-hidden="true" />
                          <td className="game-stats-table__spacer" aria-hidden="true" />
                          <td className="game-stats-table__spacer" aria-hidden="true" />
                        </>
                      ) : (
                        <>
                          <td className="game-stats-delta">
                            <GameStatsSkel className="game-stats-skel--delta" />
                          </td>
                          <td className="game-stats-delta">
                            <GameStatsSkel className="game-stats-skel--delta" />
                          </td>
                          <td className="game-stats-gem">
                            <GameStatsSkel className="game-stats-skel--gem" />
                          </td>
                          <td className="game-stats-pool">
                            <span className="game-stats-pool-btn game-stats-skel game-stats-skel--pool" />
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function GameStatsChartsSkeleton({ statsMode, onStatsModeChange }) {
  const isMemoryMode = statsMode === "memory";

  return (
    <div
      className="game-stats-view game-stats-view--charts game-stats-view--graph-panel game-stats-view--skeleton"
      aria-busy="true"
    >
      <header className="game-stats-header game-stats-header--invisible" aria-hidden="true">
        <h2 className="game-difficulty-title">Résultats et statistiques</h2>
        <p className="game-stats-recap">0 partie jouée</p>
      </header>
      <div className="game-stats-charts-block">
        <div className="game-stats-charts-panel">
          {isMemoryMode ? (
            <MemoryScoreChart history={[]} skeleton />
          ) : (
            <GameProgressChart history={[]} skeleton />
          )}
        </div>
        <div className="game-stats-charts-nav">
          <GameStatsModeSwitch mode={statsMode} onChange={onStatsModeChange} />
        </div>
      </div>
    </div>
  );
}

function GameStatsIndicatorsSkeleton({ statsMode, onStatsModeChange }) {
  return (
    <div className="game-stats-view game-stats-view--charts game-stats-view--indicators game-stats-view--skeleton">
      <header className="game-stats-header">
        <h2 className="game-difficulty-title">Résultats et statistiques</h2>
        <p className="game-stats-recap">
          <GameStatsSkel className="game-stats-skel--recap" />
        </p>
      </header>
      <div className="game-stats-charts-block">
        <div className="game-stats-charts-panel">
          <div className="game-stats-indicators-panel">
            <div className="game-stats-indicators-panel__viewport">
              <ul className="game-stats-indicators-panel__list">
                {INDICATOR_SKELETON_LABEL_WIDTHS.map((width, index) => (
                  <li key={index} className="game-stats-indicators-panel__row">
                    <span className="game-stats-indicators-panel__label">
                      <GameStatsSkel
                        className="game-stats-skel--indicator-label"
                        style={{ width: `${width}px` }}
                      />
                    </span>
                    <span className="game-stats-indicators-panel__value">
                      <GameStatsSkel className="game-stats-skel--indicator-value" />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="game-stats-charts-nav">
          <GameStatsModeSwitch mode={statsMode} onChange={onStatsModeChange} />
        </div>
      </div>
    </div>
  );
}

function GameStatsTableHead({ isMemoryMode }) {
  return (
    <thead>
      <tr>
        <th>Date</th>
        <th>Heure</th>
        <th>Niveau</th>
        <th>Score</th>
        {isMemoryMode ? (
          <>
            <th className="game-stats-table__spacer" aria-hidden="true" />
            <th className="game-stats-table__spacer" aria-hidden="true" />
            <th className="game-stats-table__spacer" aria-hidden="true" />
            <th className="game-stats-table__spacer" aria-hidden="true" />
          </>
        ) : (
          <>
            <th className="game-stats-table__discovery-th">Découverte</th>
            <th className="game-stats-table__mastery-th">Maîtrise</th>
            <th className="game-stats-table__gem-th">Gemme</th>
            <th className="game-stats-table__pool-th">
              <span
                className="vocab-tooltip-wrap game-stats-pool-info"
                tabIndex={0}
              >
                <span className="game-stats-pool-info__label">
                  Pool
                </span>
                <span
                  className="vocab-tooltip vocab-tooltip--below vocab-tooltip--wide game-stats-pool-tooltip"
                  role="tooltip"
                >
                  Chaque mot ne conserve que la trace de ses 5 dernières
                  parties. Si un mot a été rejoué depuis, son passage dans
                  cette partie a pu être écrasé : le pool reconstitué peut
                  alors être incomplet.
                </span>
              </span>
            </th>
          </>
        )}
      </tr>
    </thead>
  );
}

function formatMemoryScoreValue(value, totalQuestions = 15) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${value} / ${totalQuestions}`;
}

function formatSharePercent(count, total) {
  const denominator = Number(total) || 0;
  if (denominator <= 0) return "—";
  const value = (Math.max(0, Number(count) || 0) / denominator) * 100;
  return `${value.toLocaleString("fr-FR", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })} %`;
}

function computeMemoryDerivedStats(sessions, scoreHistory = null) {
  const sorted = [...(sessions || [])].sort((a, b) => {
    const ta = a.playedAt ? new Date(a.playedAt).getTime() : 0;
    const tb = b.playedAt ? new Date(b.playedAt).getTime() : 0;
    return tb - ta;
  });
  const last10 = sorted.slice(0, 10);
  const career =
    Array.isArray(scoreHistory) && scoreHistory.length > 0 ? scoreHistory : sorted;

  const summarize = (items) => {
    const summary = { total: items.length, tier1: 0, tier2: 0, tier3: 0 };
    for (const session of items) {
      const score = Number(session.score) || 0;
      if (score >= 12) summary.tier3 += 1;
      else if (score >= 7.5) summary.tier2 += 1;
      else summary.tier1 += 1;
    }
    return summary;
  };

  const chronological = [...career].sort((a, b) => {
    const ta = a.playedAt ? new Date(a.playedAt).getTime() : 0;
    const tb = b.playedAt ? new Date(b.playedAt).getTime() : 0;
    return ta - tb;
  });

  let longest = 0;
  let current = 0;
  for (const session of chronological) {
    if ((Number(session.score) || 0) >= 12) {
      current += 1;
      if (current > longest) longest = current;
    } else {
      current = 0;
    }
  }

  const scores = career.map((session) => Number(session.score) || 0);
  const avg10 = last10.length
    ? Math.round(
        (last10.reduce((sum, session) => sum + (Number(session.score) || 0), 0) / last10.length) *
          10
      ) / 10
    : null;

  return {
    avgScoreLast10: avg10,
    gamesTotal: career.length,
    tier3Count: summarize(career).tier3,
    longestTier3Streak: longest,
    worstScore: scores.length ? Math.min(...scores) : null,
    perfectCount: scores.filter((score) => score === 15).length,
    tiersAll: summarize(career),
    tiersLast10: summarize(last10),
  };
}

function filterSessionsByLevel(sessions, levelFilter) {
  if (levelFilter === "all") return sessions;
  const levelNum = parseInt(levelFilter, 10);
  return sessions.filter((session) => Number(session.level) === levelNum);
}

function startOfLocalDay(date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

function getSessionPlayedAt(session) {
  if (session?.playedAt) {
    const playedAt = new Date(session.playedAt);
    if (!Number.isNaN(playedAt.getTime())) return playedAt;
  }
  if (typeof session?.date === "string") {
    const match = session.date.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
    }
  }
  return null;
}

function getDateFilterRange(dateFilter, now = new Date()) {
  if (dateFilter === "all") return null;

  const todayStart = startOfLocalDay(now);

  if (dateFilter === "today") {
    const end = new Date(todayStart);
    end.setDate(end.getDate() + 1);
    return { start: todayStart, end };
  }

  if (dateFilter === "week") {
    const start = new Date(todayStart);
    const weekday = start.getDay();
    const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
    start.setDate(start.getDate() + mondayOffset);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return { start, end };
  }

  if (dateFilter === "month") {
    const start = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);
    const end = new Date(todayStart.getFullYear(), todayStart.getMonth() + 1, 1);
    return { start, end };
  }

  return null;
}

function filterSessionsByDate(sessions, dateFilter) {
  const range = getDateFilterRange(dateFilter);
  if (!range) return sessions;
  return sessions.filter((session) => {
    const playedAt = getSessionPlayedAt(session);
    if (!playedAt) return false;
    return playedAt >= range.start && playedAt < range.end;
  });
}

const STATS_MODE_LS_KEY = "vocabularium_stats_mode";
const STATS_TABLE_SCROLL_LS_KEY = "vocabularium_stats_table_scroll";
const STATS_INDICATORS_SCROLL_LS_KEY = "vocabularium_stats_indicators_scroll";
const STATS_LEVEL_FILTER_LS_KEY = "vocabularium_stats_level_filter";
const STATS_DATE_FILTER_LS_KEY = "vocabularium_stats_date_filter";

function readLevelFilter() {
  try {
    const raw = localStorage.getItem(STATS_LEVEL_FILTER_LS_KEY);
    if (raw === "all") return "all";
    const num = parseInt(raw, 10);
    return Number.isFinite(num) && num >= 1 && num <= 6 ? String(num) : "all";
  } catch {
    return "all";
  }
}

function readDateFilter() {
  try {
    const raw = localStorage.getItem(STATS_DATE_FILTER_LS_KEY);
    return raw === "today" || raw === "week" || raw === "month" ? raw : "all";
  } catch {
    return "all";
  }
}

function readTableScroll(mode) {
  try {
    const raw = JSON.parse(localStorage.getItem(STATS_TABLE_SCROLL_LS_KEY) || "{}");
    const value = Number(raw?.[mode]);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function writeTableScroll(mode, value) {
  try {
    const raw = JSON.parse(localStorage.getItem(STATS_TABLE_SCROLL_LS_KEY) || "{}");
    raw[mode] = Math.max(0, Math.round(Number(value) || 0));
    localStorage.setItem(STATS_TABLE_SCROLL_LS_KEY, JSON.stringify(raw));
  } catch {
    // quota / mode privé : on ignore
  }
}

function readIndicatorsScroll(mode) {
  try {
    const raw = JSON.parse(localStorage.getItem(STATS_INDICATORS_SCROLL_LS_KEY) || "{}");
    const value = Number(raw?.[mode]);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function writeIndicatorsScroll(mode, value) {
  try {
    const raw = JSON.parse(localStorage.getItem(STATS_INDICATORS_SCROLL_LS_KEY) || "{}");
    raw[mode] = Math.max(0, Math.round(Number(value) || 0));
    localStorage.setItem(STATS_INDICATORS_SCROLL_LS_KEY, JSON.stringify(raw));
  } catch {
    // quota / mode privé : on ignore
  }
}

function readStatsMode() {
  try {
    const raw = localStorage.getItem(STATS_MODE_LS_KEY);
    return raw === "memory" || raw === "vocab" ? raw : "vocab";
  } catch {
    return "vocab";
  }
}

function GameStatsIndicatorsScrollbar({
  trackRef,
  scrollbar,
  onTrackPointerDown,
  onThumbPointerDown,
}) {
  return (
    <div
      ref={trackRef}
      className={[
        "game-stats-indicators-panel__scrollbar",
        !scrollbar.visible && "game-stats-indicators-panel__scrollbar--hidden",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden="true"
      onPointerDown={onTrackPointerDown}
    >
      {scrollbar.visible ? (
        <div
          className="game-stats-indicators-panel__scrollbar-thumb"
          style={{
            height: `${scrollbar.height}px`,
            transform: `translateY(${scrollbar.top}px)`,
          }}
          onPointerDown={onThumbPointerDown}
        />
      ) : null}
    </div>
  );
}

function GameStatsIndicatorsPanel({ persistMode, rows }) {
  const scrollRef = useRef(null);
  const persistTimerRef = useRef(null);
  const pendingRestoreRef = useRef(true);
  const {
    trackRef,
    scrollbar,
    updateScrollbar,
    handleThumbPointerDown,
    handleTrackPointerDown,
  } = useCustomScrollbar(scrollRef);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });

  const updateScrollUi = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollEl;
    const threshold = 4;
    const hasOverflow = scrollHeight > clientHeight + 1;

    setScrollFades({
      top: hasOverflow && scrollTop > threshold,
      bottom: hasOverflow && scrollTop + clientHeight < scrollHeight - threshold,
    });

    updateScrollbar();
  }, [updateScrollbar]);

  const flushScroll = useCallback(() => {
    const scrollEl = scrollRef.current;
    if (scrollEl && scrollEl.clientHeight > 0) {
      writeIndicatorsScroll(persistMode, scrollEl.scrollTop);
    }
  }, [persistMode]);

  const tryRestoreScroll = useCallback(() => {
    if (!pendingRestoreRef.current) return;

    const scrollEl = scrollRef.current;
    if (!scrollEl || scrollEl.clientHeight === 0) return;

    const savedScrollTop = readIndicatorsScroll(persistMode);
    if (savedScrollTop > 0) {
      scrollEl.scrollTop = savedScrollTop;
    }

    pendingRestoreRef.current = false;
    updateScrollUi();
  }, [persistMode, updateScrollUi]);

  const handleScroll = useCallback(() => {
    updateScrollUi();

    if (persistTimerRef.current) {
      window.clearTimeout(persistTimerRef.current);
    }
    persistTimerRef.current = window.setTimeout(() => {
      flushScroll();
    }, 120);
  }, [updateScrollUi, flushScroll]);

  useEffect(() => {
    updateScrollUi();
  }, [rows.length, updateScrollUi]);

  useEffect(() => {
    let frame1;
    let frame2;
    frame1 = window.requestAnimationFrame(() => {
      tryRestoreScroll();
      frame2 = window.requestAnimationFrame(() => tryRestoreScroll());
    });

    return () => {
      window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
      flushScroll();
      if (persistTimerRef.current) {
        window.clearTimeout(persistTimerRef.current);
      }
    };
  }, [rows.length, tryRestoreScroll, flushScroll]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return undefined;

    const observer = new ResizeObserver(() => {
      updateScrollUi();
      tryRestoreScroll();
    });

    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [updateScrollUi, tryRestoreScroll]);

  return (
    <div className="game-stats-indicators-panel">
      <div className="game-stats-indicators-panel__viewport">
        <div className="game-stats-indicators-panel__scroller-wrap">
          <div
            ref={scrollRef}
            className="game-stats-indicators-panel__scroll"
            onScroll={handleScroll}
          >
            <ul className="game-stats-indicators-panel__list">
              {rows.map((row) => (
                <li key={row.key} className="game-stats-indicators-panel__row">
                  <span className="game-stats-indicators-panel__label">{row.label}</span>
                  <span className="game-stats-indicators-panel__value">{row.value}</span>
                </li>
              ))}
            </ul>
          </div>
          <GameStatsIndicatorsScrollbar
            trackRef={trackRef}
            scrollbar={scrollbar}
            onTrackPointerDown={handleTrackPointerDown}
            onThumbPointerDown={handleThumbPointerDown}
          />
        </div>
        {scrollFades.top ? (
          <div
            className="game-stats-indicators-panel__fade game-stats-indicators-panel__fade--top"
            aria-hidden="true"
          />
        ) : null}
        {scrollFades.bottom ? (
          <div
            className="game-stats-indicators-panel__fade game-stats-indicators-panel__fade--bottom"
            aria-hidden="true"
          />
        ) : null}
      </div>
    </div>
  );
}

export default function GameStatsView({ screen = "table", onViewSessionPool }) {
  const [sessions, setSessions] = useState([]);
  const [memorySessions, setMemorySessions] = useState([]);
  const [indicators, setIndicators] = useState(null);
  const [memoryIndicators, setMemoryIndicators] = useState(null);
  const [progressHistory, setProgressHistory] = useState([]);
  const [memoryScoreHistory, setMemoryScoreHistory] = useState([]);
  const [progressThresholds, setProgressThresholds] = useState(null);
  const [statsMode, setStatsMode] = useState(() => readStatsMode());
  const [levelFilter, setLevelFilter] = useState(() => readLevelFilter());
  const [dateFilter, setDateFilter] = useState(() => readDateFilter());
  const [tableScrollFades, setTableScrollFades] = useState({ top: false, bottom: false });
  const tableScrollRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matrixWords, setMatrixWords] = useState(null);
  const [matrixPacksConfig, setMatrixPacksConfig] = useState(null);
  const [matrixPoolFilter, setMatrixPoolFilter] = useState(() => readMatrixPoolFilter());
  const [matrixPoolScope, setMatrixPoolScope] = useState(() => readMatrixPoolScope());
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(STATS_MODE_LS_KEY, statsMode);
    } catch {
      // quota / mode privé : on ignore
    }
  }, [statsMode]);

  useEffect(() => {
    try {
      localStorage.setItem(STATS_LEVEL_FILTER_LS_KEY, levelFilter);
    } catch {
      // quota / mode privé : on ignore
    }
  }, [levelFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(STATS_DATE_FILTER_LS_KEY, dateFilter);
    } catch {
      // quota / mode privé : on ignore
    }
  }, [dateFilter]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/game/results`);
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json.message || `HTTP ${res.status}`);
        }
        if (!cancelled) {
          setSessions(json.sessions || []);
          setMemorySessions(json.memorySessions || []);
          setIndicators(json.indicators || null);
          setMemoryIndicators(json.memoryIndicators || null);
          setProgressHistory(json.progressHistory || json.indicators?.progressHistory || []);
          setMemoryScoreHistory(
            json.memoryScoreHistory || json.memoryIndicators?.scoreHistory || []
          );
          setProgressThresholds(json.progressThresholds || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Impossible de charger les statistiques.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Chargement paresseux de la liste complète des mots et de la config paquets.
  useEffect(() => {
    if (screen !== "matrix") return undefined;
    if (matrixWords !== null && matrixPacksConfig !== null) return undefined;

    let cancelled = false;
    setMatrixLoading(true);
    setMatrixError(null);

    Promise.all([
      fetch(`${API_URL}/mots-liste-detail`),
      fetch(`${API_URL}/packs`),
    ])
      .then(async ([wordsRes, packsRes]) => {
        const wordsJson = await wordsRes.json().catch(() => null);
        const packsJson = await packsRes.json().catch(() => null);
        if (!wordsRes.ok) throw new Error(`HTTP ${wordsRes.status}`);
        if (!packsRes.ok) throw new Error(`HTTP ${packsRes.status}`);
        if (cancelled) return;
        setMatrixWords(Array.isArray(wordsJson) ? wordsJson : wordsJson?.items || []);
        setMatrixPacksConfig(packsJson);
      })
      .catch((err) => {
        if (!cancelled) {
          setMatrixError(err.message || "Erreur de connexion au serveur.");
          setMatrixWords([]);
          setMatrixPacksConfig({ config: {} });
        }
      })
      .finally(() => {
        if (!cancelled) setMatrixLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [screen, matrixWords, matrixPacksConfig]);

  const filteredMatrixWords = useMemo(() => {
    const words = matrixWords || [];
    if (matrixPoolFilter === "all") return words;
    if (!matrixPacksConfig?.config) return words;
    return filterWordsForGameLevelScope(
      Number(matrixPoolFilter),
      words,
      matrixPacksConfig.config,
      matrixPoolScope
    );
  }, [matrixWords, matrixPoolFilter, matrixPoolScope, matrixPacksConfig]);

  const isMemoryMode = statsMode === "memory";
  const activeSessions = isMemoryMode ? memorySessions : sessions;

  const filteredSessions = useMemo(
    () =>
      filterSessionsByDate(
        filterSessionsByLevel(activeSessions, levelFilter),
        dateFilter
      ),
    [activeSessions, dateFilter, levelFilter]
  );

  const updateTableScrollFades = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 6;

    setTableScrollFades({
      top: scrollTop > threshold,
      bottom: scrollTop + clientHeight < scrollHeight - threshold,
    });
  }, []);

  const handleTableScroll = useCallback(() => {
    updateTableScrollFades();
    const el = tableScrollRef.current;
    if (el) writeTableScroll(statsMode, el.scrollTop);
  }, [updateTableScrollFades, statsMode]);

  useEffect(() => {
    if (screen !== "table") return undefined;
    updateTableScrollFades();
  }, [screen, filteredSessions, dateFilter, levelFilter, statsMode, updateTableScrollFades]);

  // Restaure la position de scroll mémorisée du tableau d'historique.
  useEffect(() => {
    if (screen !== "table" || loading) return undefined;
    const saved = readTableScroll(statsMode);
    const raf = requestAnimationFrame(() => {
      const el = tableScrollRef.current;
      if (el) {
        el.scrollTop = saved;
        updateTableScrollFades();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [screen, loading, statsMode, updateTableScrollFades]);

  useEffect(() => {
    if (screen !== "table") return undefined;

    const el = tableScrollRef.current;
    if (!el) return undefined;

    updateTableScrollFades();
    const observer = new ResizeObserver(updateTableScrollFades);
    observer.observe(el);
    return () => observer.disconnect();
  }, [screen, filteredSessions, dateFilter, levelFilter, statsMode, updateTableScrollFades]);

  if (screen === "matrix") {
    return (
      <div className="game-stats-view game-stats-view--matrix">
        <header className="game-stats-header game-stats-header--matrix">
          <div className="game-stats-matrix-header-row">
            <h2 className="game-stats-title game-stats-matrix__title">Matrice de maîtrise</h2>
            <MatrixPoolFilterDropdown
              value={matrixPoolFilter}
              onChange={setMatrixPoolFilter}
            />
          </div>
        </header>
        <div className="game-stats-charts-block game-stats-matrix-block">
          <div className="game-stats-charts-panel">
            <MasteryMatrixChart
              words={filteredMatrixWords}
              loading={matrixLoading}
              error={matrixError}
              poolLevel={matrixPoolFilter !== "all" ? matrixPoolFilter : null}
              poolScope={matrixPoolScope}
              onPoolScopeChange={setMatrixPoolScope}
            />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    if (screen === "indicators") {
      return (
        <GameStatsIndicatorsSkeleton
          statsMode={statsMode}
          onStatsModeChange={setStatsMode}
        />
      );
    }
    if (screen === "table") {
      return (
        <GameStatsHistorySkeleton
          statsMode={statsMode}
          onStatsModeChange={setStatsMode}
          levelFilter={levelFilter}
          dateFilter={dateFilter}
          onLevelFilterChange={setLevelFilter}
          onDateFilterChange={setDateFilter}
        />
      );
    }
    if (screen === "charts") {
      return (
        <GameStatsChartsSkeleton
          statsMode={statsMode}
          onStatsModeChange={setStatsMode}
        />
      );
    }
    return <div className="game-stats-view game-stats-loading">Chargement…</div>;
  }

  if (error) {
    return <div className="game-stats-view game-stats-error">{error}</div>;
  }

  const wheelsUsed = indicators?.wheelsUsed || {};
  const keysUsed = indicators?.keysUsed || {};
  const derivedMemory = computeMemoryDerivedStats(memorySessions, memoryScoreHistory);
  const memoryTiersAll = memoryIndicators?.tiersAll?.total != null
    ? memoryIndicators.tiersAll
    : derivedMemory.tiersAll;
  const memoryTiersLast10 = memoryIndicators?.tiersLast10?.total != null
    ? memoryIndicators.tiersLast10
    : derivedMemory.tiersLast10;
  const memoryAvgLast10 = formatAverageScore(
    memoryIndicators?.avgScoreLast10 ?? derivedMemory.avgScoreLast10
  );
  const memoryTotal = memoryIndicators?.gamesTotal ?? derivedMemory.gamesTotal;

  const nowMs = Date.now();
  const masteryLast30 = computeProgressCountDelta(
    progressHistory,
    getRollingDaysCutoffMs(30, nowMs),
    "masteredCount"
  );
  const masteryThisMonth = computeProgressCountDelta(
    progressHistory,
    getMonthStartMs(nowMs),
    "masteredCount"
  );
  const discoveryLast30 = computeProgressCountDelta(
    progressHistory,
    getRollingDaysCutoffMs(30, nowMs),
    "discoveredCount"
  );
  const discoveryThisMonth = computeProgressCountDelta(
    progressHistory,
    getMonthStartMs(nowMs),
    "discoveredCount"
  );

  const vocabIndicatorRows = [
    {
      key: "captured30",
      label: "Mots capturés — 30 derniers jours",
      value:
        indicators?.capturedLast30Days == null
          ? "—"
          : String(indicators.capturedLast30Days),
    },
    {
      key: "capturedMonth",
      label: "Mots capturés — ce mois",
      value:
        indicators?.capturedThisMonth == null
          ? "—"
          : String(indicators.capturedThisMonth),
    },
    {
      key: "mastery30",
      label: "Progression du seuil de maîtrise — 30 derniers jours",
      value:
        masteryLast30 == null ? "—" : formatProgressDelta(masteryLast30),
    },
    {
      key: "masteryMonth",
      label: "Progression du seuil de maîtrise — ce mois",
      value:
        masteryThisMonth == null ? "—" : formatProgressDelta(masteryThisMonth),
    },
    {
      key: "discovery30",
      label: "Progression du seuil de découverte — 30 derniers jours",
      value:
        discoveryLast30 == null ? "—" : formatProgressDelta(discoveryLast30),
    },
    {
      key: "discoveryMonth",
      label: "Progression du seuil de découverte — ce mois",
      value:
        discoveryThisMonth == null
          ? "—"
          : formatProgressDelta(discoveryThisMonth),
    },
    {
      key: "avg5",
      label: "Score moyen — 5 dernières parties",
      value: formatAverageScore(indicators?.avgScoreLast5),
    },
    {
      key: "avg10",
      label: "Score moyen — 10 dernières parties",
      value: formatAverageScore(indicators?.avgScoreLast10),
    },
    {
      key: "gamesToday",
      label: "Parties — aujourd'hui",
      value: String(indicators?.gamesToday ?? 0),
    },
    {
      key: "games24h",
      label: "Parties — dernières 24 h",
      value: String(indicators?.gamesLast24h ?? 0),
    },
    {
      key: "discovery5",
      label: "Découvertes — 5 dernières parties",
      value: formatProgressDelta(indicators?.discoveryLast5 ?? 0),
    },
    {
      key: "mastery5",
      label: "Maîtrise — 5 dernières parties",
      value: formatProgressDelta(indicators?.masteryLast5 ?? 0),
    },
    {
      key: "time24h",
      label: "Temps de réponse — dernières 24 h",
      value: formatPlayTime(indicators?.playTimeLast24hMs ?? 0),
    },
    {
      key: "timeToday",
      label: "Temps de réponse — aujourd'hui",
      value: formatPlayTime(indicators?.playTimeTodayMs ?? 0),
    },
    {
      key: "wheelEncouragement",
      label: "Roue d'encouragement",
      value: String(wheelsUsed.encouragement ?? 0),
    },
    {
      key: "wheelFelicitation",
      label: "Roue de félicitation",
      value: String(wheelsUsed.felicitation ?? 0),
    },
    {
      key: "keyMelusine",
      label: "Parchemin Mélusine",
      value: String(keysUsed.melusine ?? 0),
    },
    {
      key: "keyLoudiana",
      label: "Parchemin Loudiana",
      value: String(keysUsed.loudiana ?? 0),
    },
    {
      key: "keyEurydice",
      label: "Parchemin Eurydice",
      value: String(keysUsed.eurydice ?? 0),
    },
    {
      key: "keyZamselly",
      label: "Parchemin Zamselly",
      value: String(keysUsed.zamselly ?? 0),
    },
    {
      key: "keyMelitaea",
      label: "Parchemin Melitaea",
      value: String(keysUsed.melitaea ?? 0),
    },
  ];

  const memoryIndicatorRows = [
    {
      key: "memoryGamesToday",
      label: "Parties jeu de mémoire — aujourd'hui",
      value: String(memoryIndicators?.gamesToday ?? 0),
    },
    {
      key: "memoryTimeToday",
      label: "Temps de réponse — aujourd'hui",
      value: formatPlayTime(memoryIndicators?.playTimeTodayMs ?? 0),
    },
    {
      key: "memoryTime24h",
      label: "Temps de réponse — dernières 24 h",
      value: formatPlayTime(memoryIndicators?.playTimeLast24hMs ?? 0),
    },
    {
      key: "memoryAvg10",
      label: "Moyenne sur les 10 dernières parties",
      value:
        memoryAvgLast10 === "—" ? "—" : `${memoryAvgLast10} / 15`,
    },
    {
      key: "memoryTotal",
      label: "Nombre total de parties jouées",
      value: String(memoryTotal),
    },
    {
      key: "memoryMin10",
      label: "Score minimum — 10 dernières parties",
      value: formatMemoryScoreValue(memoryIndicators?.minScoreLast10),
    },
    {
      key: "memoryMax10",
      label: "Score maximum — 10 dernières parties",
      value: formatMemoryScoreValue(memoryIndicators?.maxScoreLast10),
    },
    {
      key: "memoryTier3Count",
      label: "Scores obtenus dans le 3ème tiers",
      value: String(memoryIndicators?.tier3Count ?? derivedMemory.tier3Count),
    },
    {
      key: "memoryTier3Streak",
      label: "Plus longue série dans le 3ème tiers",
      value: String(memoryIndicators?.longestTier3Streak ?? derivedMemory.longestTier3Streak),
    },
    {
      key: "memoryWorst",
      label: "Moins bon score obtenu",
      value: formatMemoryScoreValue(memoryIndicators?.worstScore ?? derivedMemory.worstScore),
    },
    {
      key: "memoryPerfect",
      label: "Scores parfaits (15/15)",
      value: String(memoryIndicators?.perfectCount ?? derivedMemory.perfectCount),
    },
    {
      key: "memoryPct3All",
      label: "Pourcentage 3ème tiers",
      value: formatSharePercent(memoryTiersAll.tier3, memoryTiersAll.total),
    },
    {
      key: "memoryPct2All",
      label: "Pourcentage 2ème tiers",
      value: formatSharePercent(memoryTiersAll.tier2, memoryTiersAll.total),
    },
    {
      key: "memoryPct1All",
      label: "Pourcentage 1er tiers",
      value: formatSharePercent(memoryTiersAll.tier1, memoryTiersAll.total),
    },
    {
      key: "memoryPct3Last10",
      label: "Pourcentage 3ème tiers — 10 dernières parties",
      value: formatSharePercent(memoryTiersLast10.tier3, memoryTiersLast10.total),
    },
    {
      key: "memoryPct2Last10",
      label: "Pourcentage 2ème tiers — 10 dernières parties",
      value: formatSharePercent(memoryTiersLast10.tier2, memoryTiersLast10.total),
    },
    {
      key: "memoryPct1Last10",
      label: "Pourcentage 1er tiers — 10 dernières parties",
      value: formatSharePercent(memoryTiersLast10.tier1, memoryTiersLast10.total),
    },
  ];

  const recordedCountLabel =
    filteredSessions.length > 1
      ? "dernières parties jouées"
      : "dernière partie jouée";

  const chartsRecapCount = isMemoryMode
    ? memoryScoreHistory.length || memoryTotal
    : progressHistory.length || sessions.length;
  const chartsRecapLabel =
    chartsRecapCount > 1 ? "parties jouées" : "partie jouée";

  const isChartsScreen = screen === "charts";
  const isIndicatorsScreen = screen === "indicators";

  return (
    <div
      className={`game-stats-view${
        isChartsScreen || isIndicatorsScreen ? " game-stats-view--charts" : ""
      }${screen === "table" ? " game-stats-view--table" : ""}${
        isChartsScreen ? " game-stats-view--graph-panel" : ""
      }${isIndicatorsScreen ? " game-stats-view--indicators" : ""}`}
    >
      {(isChartsScreen || isIndicatorsScreen) && (
        <header
          className={`game-stats-header${
            isChartsScreen ? " game-stats-header--invisible" : ""
          }`}
          aria-hidden={isChartsScreen}
        >
          <h2 className="game-difficulty-title">Résultats et statistiques</h2>
          <p className="game-stats-recap">
            {chartsRecapCount} {chartsRecapLabel}
          </p>
        </header>
      )}

      {isChartsScreen || isIndicatorsScreen ? (
        <div className="game-stats-charts-block">
          <div className="game-stats-charts-panel">
            {isIndicatorsScreen ? (
              <GameStatsIndicatorsPanel
                key={statsMode}
                persistMode={statsMode}
                rows={isMemoryMode ? memoryIndicatorRows : vocabIndicatorRows}
              />
            ) : isMemoryMode ? (
              <MemoryScoreChart history={memoryScoreHistory} />
            ) : (
              <GameProgressChart history={progressHistory} thresholds={progressThresholds} />
            )}
          </div>

          <div className="game-stats-charts-nav">
            <GameStatsModeSwitch mode={statsMode} onChange={setStatsMode} />
          </div>
        </div>
      ) : (
        <section className="game-stats-history" aria-label="Historique des parties jouées">
          <header className="game-stats-history__header">
            <div className="game-stats-history__title-row">
              <h2 className="game-stats-title game-stats-history__title">
                Historique des parties jouées
              </h2>
              <p className="game-stats-history__count">
                <strong>{filteredSessions.length}</strong> {recordedCountLabel}
              </p>
            </div>
            <div className="game-stats-history__filters">
              <div className="game-stats-history__filter">
                <div className="game-stats-history__filter-field">
                  <span className="game-stats-history__filter-label">Niveau</span>
                  <div className="game-stats-history__filter-dropdown">
                    <PackAssignDropdown
                      options={HISTORY_LEVEL_FILTER_OPTIONS}
                      value={levelFilter}
                      onChange={setLevelFilter}
                      ariaLabel="Filtrer l'historique par niveau"
                      matchTriggerWidth
                    />
                  </div>
                </div>
                <div className="game-stats-history__filter-field">
                  <span className="game-stats-history__filter-label">Date</span>
                  <div className="game-stats-history__filter-dropdown game-stats-history__filter-dropdown--date">
                    <PackAssignDropdown
                      options={HISTORY_DATE_FILTER_OPTIONS}
                      value={dateFilter}
                      onChange={setDateFilter}
                      ariaLabel="Filtrer l'historique par date"
                      matchTriggerWidth
                    />
                  </div>
                </div>
              </div>
              <div className="game-stats-history__mode">
                <GameStatsModeSwitch mode={statsMode} onChange={setStatsMode} />
              </div>
            </div>
          </header>

          <div
            className={[
              "game-stats-table-shell",
              tableScrollFades.top && "game-stats-table-shell--fade-top",
              tableScrollFades.bottom && "game-stats-table-shell--fade-bottom",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="game-stats-table-head">
              <table className="game-stats-table">
                {TABLE_COLUMNS}
                <GameStatsTableHead isMemoryMode={isMemoryMode} />
              </table>
            </div>
            <div className="game-stats-table-body">
              <div
                ref={tableScrollRef}
                className="game-stats-table-wrap"
                onScroll={handleTableScroll}
              >
                <table className="game-stats-table">
                  {TABLE_COLUMNS}
                  <tbody>
                  {filteredSessions.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="game-stats-table__empty">
                        Aucune partie enregistrée pour ces filtres.
                      </td>
                    </tr>
                  ) : isMemoryMode ? (
                    filteredSessions.map((session) => (
                      <tr key={session.id}>
                        <td>{formatSessionDate(session)}</td>
                        <td>{formatSessionTime(session)}</td>
                        <td>{session.levelName || `Niveau ${session.level}`}</td>
                        <td className="game-stats-score">
                          <strong>{session.score ?? 0}</strong>
                          <span className="game-stats-score-total">
                            {" "}
                            / {session.totalQuestions ?? "—"}
                          </span>
                        </td>
                        <td className="game-stats-table__spacer" aria-hidden="true" />
                        <td className="game-stats-table__spacer" aria-hidden="true" />
                        <td className="game-stats-table__spacer" aria-hidden="true" />
                        <td className="game-stats-table__spacer" aria-hidden="true" />
                      </tr>
                    ))
                  ) : (
                    filteredSessions.map((session) => (
                      <tr key={session.id}>
                        <td>{formatSessionDate(session)}</td>
                        <td>{formatSessionTime(session)}</td>
                        <td>{session.levelName || `Niveau ${session.level}`}</td>
                        <td className="game-stats-score">
                          <strong>{session.score ?? 0}</strong>
                          <span className="game-stats-score-total">
                            {" "}
                            / {session.totalQuestions ?? "—"}
                          </span>
                        </td>
                        <td className="game-stats-delta">
                          <ProgressDeltaCell value={session.discoveryDelta} />
                        </td>
                        <td className="game-stats-delta">
                          <ProgressDeltaCell value={session.masteryDelta} />
                        </td>
                        <HistoryGemCell session={session} />
                        <td className="game-stats-pool">
                          <button
                            type="button"
                            className="game-stats-pool-btn"
                            onClick={() => onViewSessionPool?.(session)}
                            disabled={!session.date}
                            aria-label="Voir le pool de mots de cette partie"
                          >
                            <Eye size={17} aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
